/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { empty, isString, prepareResponseData, isArray, errorResponse } from "../../Utilities/utils";
import { Toast } from "primereact/toast";

// css
import "../students/Students.css";

// api
import activityApi from "../../api/Activities";

// components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import FullPageLoader from "../../components/loader/FullPageLoader";
import InputField from "../../components/form/InputField";
import { AuthContext } from "../Root/ProtectedRoute";
import ActivitiesWrapper from "../../components/appWrapper/ActivitiesWrapper";
import SelectField from "../../components/form/SelectField";
import { activityNature } from "../../data/ActivityNature";
import Header4 from "../../components/headers/Header4";
import colors from "../../config/colors";
import { iconOptions } from "../../data/IconOptions";

const required = "This field is required!";
const validationSchema = Yup.object().shape({
  title: Yup.string().required(required),
  nature: Yup.string().required(required),
  color: Yup.string().required(required),
});

const AddUpdateActivitiesType = ({ ...props }) => {
  const { user } = useContext(AuthContext);
  const [initialValues, setInitialValues] = useState({
    title: "",
    nature: '',
    color: ''
  });
  const [iconValue, setIconValue] = useState('');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { activityTypeId } = useParams();
  const toastTR = useRef(null);

  // alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  useEffect(() => {
    if (activityTypeId) {
      getActivityType();
    }
  }, [activityTypeId]);

  /**
   * Get activity details
   * @returns 
   */
  const getActivityType = async () => {
    setIsLoading(true);
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await activityApi.getActivityType(schoolId, activityTypeId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data.response)
            ? typeof response_data.response === "string"
              ? response_data.response
              : "Something went wrong!"
            : "Failed to get class details."
        );
      }

      if (!empty(response_data) && !empty(response_data.response)) {
        const data = response_data.response;
        setInitialValues({
          title: !empty(data.title) ? data.title : "",
          nature: !empty(data.nature) ? data.nature : "",
          color: data?.color || ''
        });

        setIconValue(data?.icon || '');
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      if (empty(values)) {
        return responseDialog(
          "error",
          "Error Alert",
          "Something went wrong! Try again later."
        );
      }
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const title = !empty(values) && !empty(values.title) ? values.title : "";
      const nature = !empty(values) && !empty(values.nature) ? values.nature : "";
      const color = values?.color || "";

      if (empty(iconValue)) {
        return responseDialog(
          "error",
          "Icon is Required",
          "Please select an icon."
        );
      }
      let response;
      let activityTypeAdded = false;
      let activityTypeUpdated = false;
      if (!activityTypeId) {
        response = await activityApi.addActivityType(schoolId, title, nature, iconValue, color);
        activityTypeAdded = true;
      } else {
        response = await activityApi.updateActivityType(
          schoolId,
          activityTypeId,
          title,
          nature,
          iconValue, 
          color
        );
        activityTypeUpdated = true;
      }
      const response_data = prepareResponseData(response);
      if (!response_data?.success) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Something went wrong!"
        );
      }

      navigate("/activity-types", {
        state: { activityTypeAdded, activityTypeUpdated },
      });
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  // handle activity nature
  const handleInputType = (name, setFieldValue) => (e) => {
    const value =
      !empty(e) &&
      !empty(e.target) &&
      !empty(e.target.value) &&
      isString(e.target.value)
        ? e.target.value
        : "";
    setFieldValue(name, value);
  };

  return (
    <>
      <ActivitiesWrapper {...props}>
        <main>
          <div className="container flex-center-top">
            <MainHeader
              title={
                !empty(activityTypeId)
                  ? "Update Activity Type"
                  : "Add Activity Type"
              }
            />
            <div className="form-container mt-30">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
              {({ values, setFieldValue }) => (
                <Form style={{ width: "100%" }}>
                  <div
                    className="app_input_group"
                  >
                    <InputField
                      placeholder="Enter Activity Type Title E.g Sports, Social Activity etc"
                      name="title"
                      height={50}
                      labelTitle="Title"
                      required={true}
                    />
                    <SelectField
                      labelTitle="Activity Nature"
                      required={true}
                      placeholder="Select Activity Nature"
                      name="nature"
                      options={activityNature}
                      height={50}
                      valueKey="title"
                      selectedOption={values.nature}
                      handleChangeFunc={handleInputType(
                        "nature",
                        setFieldValue
                      )}
                    />
                  </div>
                  <div
                    className="app_input_group"
                  >
                    <InputField
                      placeholder="Select color"
                      name="color"
                      type='color'
                      containerWidth={120}
                      height={50}
                      labelTitle="Icon Color"
                      required={true}
                    />
                  </div>
                  
                  <div className="icon_toolbox_input_container">
                    <Header4>
                      <span style={{ color: colors.darkSecondary}}>
                          <strong>Select an Icon </strong>
                          <p>Selected Icon: &nbsp;&nbsp;&nbsp;<i style={{color: colors.primary}} className={`${iconValue} fs-20`}></i></p>
                      </span>
                      </Header4>
                      
                      <div className="body flex col-g-18 row-g-18 flex-wrap overflow-y-scroll">
                        {!empty(iconOptions) && isArray(iconOptions) && iconOptions.map((icon, index) => {
                          return <div key={index} className="icon-tool-box" style={{ backgroundColor: iconValue === icon?.value ? colors.primary : '', color: iconValue === icon?.value ? colors.white : ''}} onClick={() => setIconValue(icon?.value)}><i className={icon?.value} title={icon?.label}></i></div>
                        })}
                      </div>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <ButtonIcon
                      height={45}
                      marginTop={5}
                      color="#ffffff"
                      backgroundColor="#633ccd"
                      width={300}
                      borderColor="#633ccd"
                      buttonText={
                        activityTypeId ? "Update Activity Type" : "Add Activity Type"
                      }
                      type="submit"
                    />
                  </div>
                </Form>
              )}
              </Formik>
            </div>
          </div>
        </main>
        {isLoading && <FullPageLoader visible={isLoading} />}
        <Toast ref={toastTR} position="bottom-left" />
      </ActivitiesWrapper>
    </>
  );
};

export default AddUpdateActivitiesType;
